import { useTranslation } from "react-i18next";
import FileCategorySingleSelect from "components/file-category-single-select/file-category-single-select";
import SearchableFilesTypesSelect from "components/searchable-files-types-select/searchable-files-types-select";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  Toggle,
  useInlineEdit,
  useSaveWithToast,
} from "./ui";
import { SettingNames } from "./use-setting-names";

export type EvidenceCheck = {
  enabled: boolean;
  /** Where the proof file goes once the check is done. */
  categoryId: string;
  fileTypeId: string;
};

type EvidenceCheckRowProps = {
  label: string;
  description: string;
  check: EvidenceCheck;
  names: SettingNames;
  /** Saves the whole check; the row shows the result of that save. */
  onSave: (next: EvidenceCheck) => Promise<unknown>;
  /** Another row's save is running; keep this row's switch and Save off until it is done. */
  locked?: boolean;
};

/**
 * One check a customer goes through before the financial terms are approved:
 * an on/off switch, and while it is on, where the proof of the check is filed.
 */
export function EvidenceCheckRow({
  label,
  description,
  check,
  names,
  onSave,
  locked,
}: EvidenceCheckRowProps) {
  const { t } = useTranslation();
  const proofEdit = useInlineEdit({
    categoryId: check.categoryId,
    fileTypeId: check.fileTypeId,
  });
  const { save, saving, error } = useSaveWithToast();

  const saveCheck = async (next: EvidenceCheck) => {
    if (await save(() => onSave(next))) proofEdit.cancel();
  };

  const busy = saving || locked;

  const proofPlaceNames = [
    names.categoryName(check.categoryId),
    names.fileTypeName(check.fileTypeId),
  ]
    .filter(Boolean)
    .join(" · ");
  // "None" is only true when nothing is chosen; a chosen place whose name did
  // not load is left unsaid rather than reported as missing.
  const isProofPlaceChosen = !!(check.categoryId || check.fileTypeId);
  const proofPlace =
    proofPlaceNames || (isProofPlaceChosen ? null : t("settings_none"));

  return (
    <SettingRow
      label={label}
      description={description}
      value={
        check.enabled &&
        names.ready &&
        proofPlace &&
        t("settings_check_proof_place", { place: proofPlace })
      }
      action={
        <div className="flex items-center gap-4 max-md:gap-5">
          {check.enabled && !proofEdit.editing && (
            <ChangeLink onClick={proofEdit.start} />
          )}
          <Toggle
            label={label}
            checked={check.enabled}
            disabled={busy}
            onChange={(enabled) => saveCheck({ ...check, enabled })}
          />
        </div>
      }
    >
      {proofEdit.editing && (
        <EditPanel
          saving={busy}
          error={error}
          onSave={() => saveCheck({ ...check, ...proofEdit.draft })}
          onCancel={proofEdit.cancel}
        >
          <div className="space-y-3">
            <FileCategorySingleSelect
              value={
                proofEdit.draft.categoryId ? [proofEdit.draft.categoryId] : []
              }
              onChange={(ids) =>
                proofEdit.setDraft({
                  ...proofEdit.draft,
                  categoryId: ids?.[0] ?? "",
                })
              }
              subject="customer"
            />
            <SearchableFilesTypesSelect
              value={proofEdit.draft.fileTypeId}
              onChange={(fileTypeId) =>
                proofEdit.setDraft({ ...proofEdit.draft, fileTypeId })
              }
            />
          </div>
        </EditPanel>
      )}
    </SettingRow>
  );
}
